import {Navigate, Route, Routes} from "react-router-dom";
import {getDefaultPathForCapabilities} from "../shared/navigation/navigation";
import {OverviewPage} from "../features/overview/OverviewPage";
import {OrganizationsPage} from "../features/organizations/OrganizationsPage";
import {OrganizationDetailPage} from "../features/organizations/OrganizationDetailPage";
import {BranchDetailPage} from "../features/organizations/BranchDetailPage";
import {OrganizationEditorPage} from "../features/organizations/OrganizationEditorPage";
import {OrganizationPositionsPage} from "../features/organizations/OrganizationPositionsPage";
import {UsersWalletsPage} from "../features/users/UsersWalletsPage";
import {UserProfilePage} from "../features/users/UserProfilePage";
import {FinancePage} from "../features/finance/FinancePage";
import {SettlementsPage} from "../features/settlements/SettlementsPage";
import {SettlementDetailPage} from "../features/settlements/SettlementDetailPage";
import {SettlementExportsPage} from "../features/settlements/SettlementExportsPage";
import {SettlementExportDetailPage} from "../features/settlements/SettlementExportDetailPage";
import {RefundsPage} from "../features/refunds/RefundsPage";
import {RefundDetailPage} from "../features/refunds/RefundDetailPage";
import {RefundHistoryPage} from "../features/refunds/RefundHistoryPage";
import {RefundPolicyPage} from "../features/refunds/RefundPolicyPage";
import {SplitBillsPage} from "../features/split-bills/SplitBillsPage";
import {SplitBillCreatePage} from "../features/split-bills/SplitBillCreatePage";
import {SplitBillDetailPage} from "../features/split-bills/SplitBillDetailPage";
import {ReferralsPage, ReferralNetworkPage} from "../features/referrals/ReferralsPage";
import {ReferralPoliciesPage} from "../features/referralPolicies/ReferralPoliciesPage";
import {ClubPage} from "../features/club/ClubPage";
import {ContentPage} from "../features/content/ContentPage";
import {InboxPage} from "../features/inbox/InboxPage";
import {DeployersPage} from "../features/deployers/DeployersPage";
import {AgentDashboardPage} from "../features/agent/AgentDashboardPage";
import {AuditPage} from "../features/audit/AuditPage";
import {SystemPage} from "../features/system/SystemPage";
import {ApplicationQueuePage} from "../features/business-onboarding/ApplicationQueuePage";
import {ApplicationDetailPage} from "../features/business-onboarding/ApplicationDetailPage";
import {BusinessOnboardingWorkspace} from "../features/business-onboarding/BusinessOnboardingWorkspace";
import {OnboardingRouteGuard} from "../features/business-onboarding/components/OnboardingRouteGuard";
import {AdminRouteGuard} from "../shared/navigation/AdminRouteGuard";
import {ADMIN_ROLES} from "../shared/auth/roles";

const PLATFORM_ROLES = [ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR];
const PLACE_CREATOR_ROLES = [...PLATFORM_ROLES, ADMIN_ROLES.DEPLOYER, ADMIN_ROLES.IMPLEMENTATION_HEAD];
const ORGANIZATION_MANAGER_ROLES = [
    ...PLATFORM_ROLES,
    ADMIN_ROLES.OWNER,
    ADMIN_ROLES.DEPLOYER,
    ADMIN_ROLES.IMPLEMENTATION_HEAD,
];
const REVIEW_ROLES = [ADMIN_ROLES.OWNER];

export const AppRoutes = ({role, capabilities = {}, countryScope}) => {
    const access = {
        role,
        modules: capabilities.modules || [],
        actions: capabilities.actions || [],
    };
    const defaultPath = getDefaultPathForCapabilities(access);

    return (
        <Routes>
            <Route element={<AdminRouteGuard {...access} sectionKey="overview" />}>
                <Route path="/overview" element={<OverviewPage />} />
            </Route>

            <Route
                element={(
                    <AdminRouteGuard
                        {...access}
                        sectionKey="organizations"
                        allowedRoles={access.actions.includes("organization.manage") ? PLATFORM_ROLES : []}
                    />
                )}
            >
                <Route path="/employers/change_positions" element={<OrganizationPositionsPage />} />
            </Route>
            <Route
                element={(
                    <AdminRouteGuard
                        {...access}
                        sectionKey="organizations"
                        allowedRoles={access.actions.includes("organization.manage") ? PLACE_CREATOR_ROLES : []}
                    />
                )}
            >
                <Route path="/employers/add" element={<OrganizationEditorPage mode="place" />} />
                <Route path="/employers/:employer_id/edit" element={<OrganizationEditorPage mode="place" />} />
            </Route>
            <Route
                element={(
                    <AdminRouteGuard
                        {...access}
                        sectionKey="organizations"
                        allowedRoles={
                            access.actions.includes("organization.manage")
                                ? ORGANIZATION_MANAGER_ROLES
                                : []
                        }
                    />
                )}
            >
                <Route path="/employers/:employer_id/branch/add" element={<OrganizationEditorPage mode="branch" />} />
                <Route path="/employers/:employer_id/branch/:branch_id/edit" element={<OrganizationEditorPage mode="branch" />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="organizations" />}>
                <Route path="/employers/:employer_id/branch/:branch_id" element={<BranchDetailPage />} />
                <Route path="/employers/:employer_id" element={<OrganizationDetailPage />} />
                <Route path="/employers" element={<OrganizationsPage />} />
            </Route>

            <Route element={<OnboardingRouteGuard {...access} />}>
                <Route
                    path="/business-onboarding"
                    element={<BusinessOnboardingWorkspace initialCountryScope={countryScope} />}
                >
                    <Route index element={<ApplicationQueuePage />} />
                    <Route
                        path="applications/:applicationId"
                        element={<ApplicationDetailPage />}
                    />
                </Route>
            </Route>

            <Route element={<AdminRouteGuard {...access} sectionKey="users" />}>
                <Route path="/users/:user_id" element={<UserProfilePage />} />
                <Route path="/users" element={<UsersWalletsPage />} />
            </Route>

            <Route element={<AdminRouteGuard {...access} sectionKey="finance" />}>
                <Route path="/reports" element={<FinancePage />} />
                <Route path="/finance/refunds" element={<RefundsPage />} />
                <Route path="/finance/refunds/:requestId" element={<RefundDetailPage />} />
                <Route path="/finance/refund-history" element={<RefundHistoryPage />} />
                <Route path="/finance/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/finance/split-bills" element={<SplitBillsPage />} />
                <Route path="/finance/split-bills/new" element={<SplitBillCreatePage />} />
                <Route path="/finance/split-bills/:groupId" element={<SplitBillDetailPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="settlements" />}>
                <Route path="/settlements" element={<SettlementsPage />} />
                <Route path="/settlements/exports" element={<SettlementExportsPage />} />
                <Route path="/settlements/exports/:exportId" element={<SettlementExportDetailPage />} />
                <Route path="/settlements/:settlementId" element={<SettlementDetailPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="referrals" />}>
                <Route path="/referral/policies" element={<ReferralPoliciesPage />} />
                <Route path="/referral/:ref_id" element={<ReferralNetworkPage />} />
                <Route path="/referral" element={<ReferralsPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="club" />}>
                <Route path="/club" element={<ClubPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="content" />}>
                <Route path="/news" element={<ContentPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="inbox" />}>
                <Route path="/notifications" element={<InboxPage />} />
            </Route>
            <Route
                element={(
                    <AdminRouteGuard
                        {...access}
                        sectionKey="inbox"
                        allowedRoles={REVIEW_ROLES}
                    />
                )}
            >
                <Route path="/reviews" element={<InboxPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="deployers" />}>
                <Route path="/deployers" element={<DeployersPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="agent" />}>
                <Route path="/agent" element={<AgentDashboardPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="audit" />}>
                <Route path="/audit" element={<AuditPage />} />
            </Route>
            <Route element={<AdminRouteGuard {...access} sectionKey="system" />}>
                <Route path="/system" element={<SystemPage />} />
            </Route>

            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
    );
};
